import React, { useState } from 'react';
import { PlusIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { StudySession } from '../../types/educational-chat';
import { useTheme } from '../../contexts/ThemeContext';

interface ChatSidebarProps {
  sessions: StudySession[];
  activeSessionId: string | null;
  onNewChat: () => void;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  sessions,
  activeSessionId,
  onNewChat,
  onSelectSession,
  onDeleteSession
}) => {
  const { isDarkMode } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredSession, setHoveredSession] = useState<string | null>(null);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Group sessions by date
  const groupSessionsByDate = (sessions: StudySession[]) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups: { [key: string]: StudySession[] } = {
      'Today': [],
      'Yesterday': [],
      'Previous 7 Days': [],
      'Older': []
    };

    sessions.forEach(session => {
      const sessionDate = new Date(session.last_activity);
      if (sessionDate.toDateString() === today.toDateString()) {
        groups['Today'].push(session);
      } else if (sessionDate.toDateString() === yesterday.toDateString()) {
        groups['Yesterday'].push(session);
      } else if (sessionDate > weekAgo) {
        groups['Previous 7 Days'].push(session);
      } else {
        groups['Older'].push(session);
      }
    });

    return groups;
  };

  const groupedSessions = groupSessionsByDate(sessions);

  return (
    <div className={`chat-sidebar ${isCollapsed ? 'collapsed' : ''} ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      {/* Header */}
      <div className="sidebar-header">
        {!isCollapsed && <h2 className="sidebar-title">Chats</h2>}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="collapse-button"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRightIcon className="icon" />
          ) : (
            <ChevronLeftIcon className="icon" />
          )}
        </button>
      </div>

      {/* New Chat Button */}
      <div className="sidebar-section">
        <button
          onClick={onNewChat}
          className="new-chat-button"
        >
          <PlusIcon className="icon" />
          {!isCollapsed && <span>New chat</span>}
        </button>
      </div>

      {/* Sessions List */}
      <div className="sessions-container">
        {!isCollapsed && sessions.length === 0 && (
          <div className="empty-sessions">
            <ChatBubbleLeftRightIcon className="empty-icon" />
            <p>No conversations yet</p>
            <span>Start a new chat to begin</span>
          </div>
        )}

        {Object.entries(groupedSessions).map(([groupName, groupSessions]) => {
          if (groupSessions.length === 0) return null;

          return (
            <div key={groupName} className="session-group">
              {!isCollapsed && (
                <h3 className="group-title">{groupName}</h3>
              )}
              {groupSessions.map(session => (
                <div
                  key={session.id}
                  className={`session-item ${session.id === activeSessionId ? 'active' : ''}`}
                  onClick={() => onSelectSession(session.id)}
                  onMouseEnter={() => setHoveredSession(session.id)}
                  onMouseLeave={() => setHoveredSession(null)}
                  title={isCollapsed ? session.title || 'New conversation' : undefined}
                >
                  {isCollapsed ? (
                    <div className="session-icon">
                      <span>{(session.title || 'New')[0].toUpperCase()}</span>
                    </div>
                  ) : (
                    <>
                      <div className="session-content">
                        <h4 className="session-title">
                          {session.title || 'New conversation'}
                        </h4>
                        <p className="session-meta">
                          {formatTimestamp(session.last_activity)}
                        </p>
                      </div>
                      {hoveredSession === session.id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteSession(session.id);
                          }}
                          className="delete-button"
                          title="Delete conversation"
                        >
                          <TrashIcon className="icon" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <style>{`
        .chat-sidebar {
          width: 260px;
          height: 100%;
          background: rgba(15, 23, 42, 0.95);
          background-image: radial-gradient(circle, rgba(51, 65, 85, 0.2) 1.2px, transparent 1.2px);
          background-size: 24px 24px;
          border-right: 1px solid rgba(100, 116, 139, 0.2);
          display: flex;
          flex-direction: column;
          transition: width 0.3s ease;
          position: relative;
          backdrop-filter: blur(12px);
        }

        .chat-sidebar.light-theme {
          background: rgba(255, 255, 255, 0.95);
          background-image: radial-gradient(circle, rgba(209, 213, 219, 0.4) 1.2px, transparent 1.2px);
          border-right: 1px solid rgba(209, 213, 219, 0.5);
        }

        .chat-sidebar.collapsed {
          width: 60px;
        }

        /* Header */
        .sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 16px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
        }

        .light-theme .sidebar-header {
          border-bottom: 1px solid rgba(209, 213, 219, 0.5);
        }

        .sidebar-title {
          font-size: 20px;
          font-weight: 600;
          color: #F8FAFC;
          margin: 0;
        }

        .light-theme .sidebar-title {
          color: #1F2937;
        }

        .collapse-button {
          width: 28px;
          height: 28px;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s;
          color: #94A3B8;
        }

        .light-theme .collapse-button {
          color: #6B7280;
        }

        .collapse-button:hover {
          background: rgba(100, 116, 139, 0.2);
          color: #06B6D4;
        }

        .light-theme .collapse-button:hover {
          background: rgba(6, 182, 212, 0.1);
          color: #0891B2;
        }

        .icon {
          width: 18px;
          height: 18px;
        }

        /* New Chat Button */
        .sidebar-section {
          padding: 12px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
        }

        .light-theme .sidebar-section {
          border-bottom: 1px solid rgba(209, 213, 219, 0.5);
        }

        .new-chat-button {
          width: 100%;
          padding: 10px;
          background: linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%);
          color: white;
          border: none;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 0 20px rgba(6, 182, 212, 0.25);
        }

        .light-theme .new-chat-button {
          box-shadow: 0 4px 12px rgba(6, 182, 212, 0.2);
        }

        .new-chat-button:hover {
          box-shadow: 0 0 30px rgba(6, 182, 212, 0.4);
          transform: translateY(-1px);
        }

        .light-theme .new-chat-button:hover {
          box-shadow: 0 6px 20px rgba(6, 182, 212, 0.3);
        }

        .collapsed .new-chat-button {
          padding: 10px 0;
        }

        /* Sessions Container */
        .sessions-container {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
        }

        /* Empty State */
        .empty-sessions {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 32px 16px;
          text-align: center;
          color: #94A3B8;
        }

        .light-theme .empty-sessions {
          color: #6B7280;
        }

        .empty-icon {
          width: 48px;
          height: 48px;
          margin-bottom: 12px;
          opacity: 0.5;
          color: #64748B;
        }

        .light-theme .empty-icon {
          color: #9CA3AF;
        }

        .empty-sessions p {
          font-size: 14px;
          font-weight: 500;
          margin: 0 0 4px 0;
          color: #94A3B8;
        }

        .light-theme .empty-sessions p {
          color: #6B7280;
        }

        .empty-sessions span {
          font-size: 12px;
          color: #64748B;
        }

        .light-theme .empty-sessions span {
          color: #9CA3AF;
        }

        /* Session Groups */
        .session-group {
          margin-bottom: 20px;
        }

        .group-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748B;
          margin: 0 0 8px 8px;
        }

        .light-theme .group-title {
          color: #9CA3AF;
        }

        /* Session Items */
        .session-item {
          padding: 10px 12px;
          margin-bottom: 2px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          min-height: 44px;
        }

        .session-item:hover {
          background: rgba(100, 116, 139, 0.15);
        }

        .light-theme .session-item:hover {
          background: rgba(6, 182, 212, 0.05);
        }

        .session-item.active {
          background: rgba(6, 182, 212, 0.1);
          border: 1px solid rgba(6, 182, 212, 0.2);
        }

        .light-theme .session-item.active {
          background: rgba(6, 182, 212, 0.08);
          border: 1px solid rgba(6, 182, 212, 0.25);
        }

        .session-icon {
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: rgba(100, 116, 139, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          color: #94A3B8;
          flex-shrink: 0;
        }

        .light-theme .session-icon {
          background: rgba(6, 182, 212, 0.1);
          color: #0891B2;
        }

        .session-item.active .session-icon {
          background: linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%);
          color: white;
        }

        .session-content {
          flex: 1;
          min-width: 0;
        }

        .session-title {
          font-size: 14px;
          font-weight: 500;
          color: #E2E8F0;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .light-theme .session-title {
          color: #1F2937;
        }

        .session-item.active .session-title {
          color: #06B6D4;
        }

        .light-theme .session-item.active .session-title {
          color: #0891B2;
        }

        .session-meta {
          font-size: 12px;
          color: #64748B;
          margin: 2px 0 0 0;
        }

        .light-theme .session-meta {
          color: #9CA3AF;
        }

        .delete-button {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border: none;
          background: rgba(100, 116, 139, 0.2);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          color: #94A3B8;
        }

        .light-theme .delete-button {
          background: rgba(107, 114, 128, 0.1);
          color: #6B7280;
        }

        .delete-button:hover {
          background: rgba(220, 38, 38, 0.2);
          color: #FCA5A5;
        }

        .light-theme .delete-button:hover {
          background: rgba(220, 38, 38, 0.1);
          color: #DC2626;
        }

        .delete-button .icon {
          width: 16px;
          height: 16px;
        }

        /* Scrollbar */
        .sessions-container::-webkit-scrollbar {
          width: 6px;
        }

        .sessions-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .sessions-container::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.2);
          border-radius: 999px;
        }

        .light-theme .sessions-container::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
        }

        .sessions-container::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 116, 139, 0.4);
        }

        .light-theme .sessions-container::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
      `}</style>
    </div>
  );
};

export default ChatSidebar;
